/**
 * Which image a placement card shows for its decoration.
 *
 * Shared by VariantCard (the card thumbnail) and the split light/dark dialog
 * (the decoration preview), so both surfaces show the same picture of the
 * same decoration rather than two resolution orders that drift.
 */
import { isEmbroiderySource } from "@/utils/artworkUploadContract";
import { previewDisplay } from "@/utils/artworkPreviewState";

// Thumbnail URL resolution -- prefer the server-generated thumbnail
// (256x256 fit-within) when the resolved Artwork has one; fall back to
// the full image_url; render the placeholder tile when both are absent.
// The legacy /api/decoration_artifacts/<id>/thumbnail endpoint was a
// dead path (no backend route ever shipped) -- the broken URL produced
// the SPA's index.html and rendered a blank img.
// `image_url` legitimately holds the SOURCE BYTES for a stitch file -- the
// column is documented as format-agnostic and the colorway endpoints depend
// on those bytes staying addressable -- but source bytes are not an image,
// and a Tajima DST in an <img> renders nothing at all.
//
// Reuses the SHARED `isEmbroiderySource` (the same predicate the bind-surface
// method lock uses) rather than a second extension list that could drift from
// it. The only thing added here is stripping a query string, because this
// checks a URL as well as a filename and a signed S3 URL carries one.
function isStitchSource(url, filename) {
    const path = String(url || "").split("?")[0];
    return isEmbroiderySource(path) || isEmbroiderySource(filename);
}

// WHICH IMAGE THIS CARD ACTUALLY LOADS.
//
// Jake, 2026-09-02: "The broken image ... is also an attempt to load the .dst
// file into the <img> tag. That clearly won't work. We need to load a
// colorway preview instead."
//
// Measured on 8089 before the change, for all three DST artworks (4, 8, 12):
// `thumbnail_url` was NULL, `image_url` was the raw stitch file, and an
// `artwork_preview` sidecar was already `ready` with a real PNG. The old
// order (thumbnail -> image_url) therefore always fell through to the DST
// while the rendered PNG sat unused.
//
// The sidecar arrives on the VARIANT, not the artwork: the product-editor
// store hydrates it from POST /api/artworks/preview_states and stamps it
// onto `variant.preview` (see productEditor.js `_hydratePreviewStates`).
// The SAFRS artworks payload deliberately cannot carry it -- a read
// serialization must not commit the lazy-render enqueue -- so reading it off
// `artwork` would always be null. Verified against the live API.
//
// State handling goes through the SHARED `previewDisplay` helper, the same
// one the Stage uses, rather than a second copy of the state machine here.
// It already encodes the case that matters most: a row marked `ready` but
// carrying no URL falls back to the spinner rather than rendering a broken
// <img>.
//
// The order:
//   1. thumbnail_url -- a real raster thumbnail beats everything.
//   2. the sidecar preview, when it resolves to a ready URL.
//   3. image_url, UNLESS it is a stitch source. Rung 3 is what keeps every
//      ordinary raster artwork working exactly as before.
//
// Returning null lands on the existing "No artwork" tile, which is the
// honest answer for "this is embroidery and its preview is not ready yet" --
// strictly better than a broken-image icon, which reads as a fault.
export function variantThumbnailUrl(variant, artwork) {
    const art = artwork;
    if (art?.thumbnail_url) return art.thumbnail_url;

    // `variant.preview` is the hydrated sidecar; `artwork.preview` is
    // accepted too so a caller that already has the artwork-shaped payload
    // (the decoration-library surfaces) keeps working.
    const shown = previewDisplay(variant?.preview ?? art?.preview);
    if (shown.kind === "ready") return shown.previewUrl;

    if (art?.image_url && !isStitchSource(art.image_url, art.filename)) {
        return art.image_url;
    }
    return null;
}
